import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, Select as MuiSelect, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { toast } from 'sonner';
import { handlePreventKeyDownDecimal } from '../../utility/helpers/commonFunction';
import { NumericEngine, Raw } from '../../utility/helpers/numericEngine';

const DiscountComponent = ({ isHide = false, discountId, setDiscountId, setSelectedDiscount, totals, setTotals, discountList = [] }) => {
    const [discountError, setDiscountError] = useState(false);

    const handleDiscountChange = (value) => {
        const selectedDiscount = discountList.find((item) => item._id === value.target.value);
        setSelectedDiscount(selectedDiscount);
        const discountPercentage = selectedDiscount?.discount?.toFixed(2) || 0;

        setDiscountId(value.target.value);

        const discountValue = parseFloat((totals?.subTotal * discountPercentage) / 100).toFixed(2);

        const tax = totals?.taxPercentage > 0 ? ((totals?.subTotal - discountValue) * totals?.taxPercentage) / 100 : 0;
        const estimatedTotal = totals?.subTotal - discountValue + tax;

        setTotals({
            ...totals,
            discountPercentage,
            discount: discountValue,
            tax,
            estimatedTotal,
        });

        // Validate discount
        const isValidDiscount = totals?.subTotal === 0 || discountValue <= totals?.subTotal;
        setDiscountError(!isValidDiscount);
    };

    return (
        <div style={{ width: '100%' }}>
            <Grid container item justifyContent={'space-between'} paddingTop={'24px'} paddingBottom={'24px'} rowSpacing={2} spacing={2}>
                {isHide ? (
                    <Grid item></Grid>
                ) : (
                    <Grid item xs={12} md={4.2} lg={3.1}>
                        <FormControl fullWidth lineHeight={'24px'}>
                            <InputLabel htmlFor="outlined-adornment-search" lineHeight={'24px'}>
                                Discount Program
                            </InputLabel>
                            <MuiSelect
                                style={{ width: '100%' }}
                                value={discountId || ''}
                                onChange={(value) => {
                                    handleDiscountChange(value);
                                }}
                                id="outlined-password-input"
                                label="Discount Program"
                            >
                                <MenuItem value="none">
                                    <div>None</div>
                                </MenuItem>
                                {discountList.map((item, index) => (
                                    <MenuItem key={index} value={item?._id}>
                                        <Stack direction={'row'} justifyContent={'space-between'} sx={{ width: '100%' }}>
                                            <div> {item.programName}</div>
                                            <Chip
                                                sx={{
                                                    background: '#EBEBEB',
                                                    color: '#000',
                                                }}
                                                size="small"
                                                label={`${item.discount}%`}
                                            />
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </MuiSelect>
                        </FormControl>
                    </Grid>
                )}
                <Grid item xs={12} md={7.8} lg={5.6}>
                    <Stack direction="row" spacing={4} justifyContent={'flex-end'}>
                        <Stack direction="column">
                            <TextField
                                variant="outlined"
                                size="medium"
                                label="Discount(%)"
                                color={discountError ? 'error' : 'primary'}
                                value={totals?.discountPercentage}
                                disabled={discountId && discountId !== '' && discountId !== 'none' ? true : totals?.subTotal == 0 ? true : false}
                                onChange={(e) => {
                                    const inputValue = e.target.value;
                                    let numericValue = inputValue.replace(/[^\d.]/g, '');

                                    if (numericValue.length > 1 && numericValue.startsWith('0') && /[1-9a-zA-Z]/.test(numericValue[1])) {
                                        numericValue = numericValue.slice(1);
                                    }
                                    if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
                                        // Validate percentage
                                        if (Number(numericValue) > 100) {
                                            setDiscountError(true);
                                            return false;
                                        } else {
                                            setDiscountError(false);
                                        }

                                        // Calculate discount amount from percentage
                                        const discountPercentage = Number(numericValue) || 0; // "1." → 1
                                        const subTotal = Number(totals?.subTotal) || 0;

                                        const discountValue = NumericEngine.divide(Raw.mul(subTotal, discountPercentage), 100);

                                        // Calculate taxable amount
                                        const taxableAmount = NumericEngine.subtract(subTotal, discountValue);

                                        // Calculate tax on taxable amount
                                        const taxPercentage = Number(totals?.taxPercentage) || 0;
                                        const tax = taxPercentage > 0 ? NumericEngine.multiply(Raw.div(taxPercentage, 100), taxableAmount) : '0';

                                        const total = NumericEngine.add(taxableAmount, tax);

                                        setTotals({
                                            discountPercentage: numericValue,
                                            discount: discountValue,
                                            tax: tax,
                                            estimatedTotal: total,
                                            taxPercentage: totals?.taxPercentage > 0 ? totals?.taxPercentage : 0,
                                            subTotal: totals?.subTotal,
                                        });
                                    }
                                }}
                                onKeyDown={handlePreventKeyDownDecimal}
                            />
                            {discountError && (
                                <p
                                    style={{
                                        color: '#d32f2f',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.03333em',
                                        lineHeight: '1.66',
                                        marginTop: '3px',
                                    }}
                                >
                                    Discount cannot exceed 100% or be less than 0.
                                </p>
                            )}
                        </Stack>
                        <Stack direction="column">
                            <TextField
                                variant="outlined"
                                size="medium"
                                label="Discount($)"
                                color={discountError ? 'error' : 'primary'}
                                value={totals?.discount}
                                disabled={discountId && discountId !== '' && discountId !== 'none' ? true : totals?.subTotal == 0 ? true : false}
                                onChange={(e) => {
                                    const inputValue = e.target.value;
                                    let numericValue = inputValue.replace(/[^\d.]/g, '');

                                    if (numericValue.length > 1 && numericValue.startsWith('0') && /[1-9a-zA-Z]/.test(numericValue[1])) {
                                        numericValue = numericValue.slice(1);
                                    }
                                    if (numericValue === '' || /^\d*(\.\d{0,2})?$/.test(numericValue)) {
                                        // Calculate percentage based on amount
                                        const discountAmount = Number(numericValue) || 0; // "1." → 1
                                        const subTotal = Number(totals?.subTotal) || 0;

                                        const discountPercentage = subTotal > 0 ? NumericEngine.divide(Raw.mul(discountAmount, 100), subTotal) : '0';

                                        // Validate percentage
                                        if (Number(discountPercentage) > 100) {
                                            setDiscountError(true);
                                            return false;
                                        } else {
                                            setDiscountError(false);
                                        }

                                        // Calculate taxable amount
                                        const taxableAmount = NumericEngine.subtract(NumericEngine.add(subTotal, 0), discountAmount);

                                        // Calculate tax on taxable amount
                                        const taxPercentage = Number(totals?.taxPercentage) || 0;
                                        const tax = taxPercentage > 0 ? NumericEngine.multiply(NumericEngine.divide(taxPercentage, 100), taxableAmount) : '0';

                                        const total = NumericEngine.add(taxableAmount, tax);

                                        setTotals((prevPaymentDetails) => ({
                                            ...prevPaymentDetails,
                                            discount: numericValue, // Keep original string
                                            tax: Number(tax),
                                            discountPercentage: Number(discountPercentage),
                                            total: Number(total),
                                        }));

                                        if (discountAmount > subTotal) {
                                            setDiscountError(true);
                                        } else {
                                            setDiscountError(false);
                                        }
                                    }
                                }}
                                onKeyDown={handlePreventKeyDownDecimal}
                            />
                            {discountError && (
                                <p
                                    style={{
                                        color: '#d32f2f',
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.03333em',
                                        lineHeight: '1.66',
                                        marginTop: '3px',
                                    }}
                                >
                                    Discount can not be more than subtotal amount
                                </p>
                            )}
                        </Stack>
                        <TextField
                            variant="outlined"
                            size="medium"
                            value={totals?.taxPercentage}
                            label="Taxes (%)"
                            onChange={(e) => {
                                let inputValue = e.target.value;

                                // Remove leading zeros but keep decimal points
                                if (inputValue.length > 1 && inputValue.startsWith('0') && /[1-9a-zA-Z]/.test(inputValue[1])) {
                                    inputValue = inputValue.slice(1);
                                }

                                // Allow only numbers and one decimal point with max 2 decimal places
                                if (/^\d*\.?\d{0,3}$/.test(inputValue)) {
                                    const taxPercentage = Number(inputValue) || 0; // "1." → 1
                                    const { subTotal, discount, discountPercentage } = totals;
                                    const hasDiscount = discountPercentage > 0 || discount > 0;

                                    // Calculate discount and tax
                                    const discountValue = hasDiscount ? NumericEngine.divide(NumericEngine.multiply(subTotal, discountPercentage), 100) : 0;

                                    // Calculate taxable amount
                                    const taxableAmount = NumericEngine.subtract(NumericEngine.add(subTotal, 0), discountValue);

                                    const tax = taxPercentage > 0 ? NumericEngine.multiply(Raw.div(taxPercentage, 100), taxableAmount) : '0';

                                    const total = NumericEngine.add(taxableAmount, tax);

                                    setTotals({
                                        ...totals,
                                        taxPercentage: inputValue,
                                        discountPercentage: hasDiscount ? discountPercentage : 0,
                                        tax: tax,
                                        estimatedTotal: total,
                                    });
                                } else {
                                    toast.error('Please enter a valid number');
                                }
                            }}
                        />
                    </Stack>
                </Grid>
            </Grid>

            <Grid container item justifyContent={'end'} padding={'0px 23px 24px 24px'}>
                <Grid item>
                    <Stack direction="row" spacing={2}>
                        <Box textAlign={'end'}>
                            <Typography mb={1}>Sub Total</Typography>

                            <Typography mb={1}>Discount</Typography>

                            <Typography mb={1}>Tax</Typography>
                            <Typography mb={1}> Total</Typography>
                        </Box>
                        <Box textAlign={'end'}>
                            <Typography mb={1}>
                                {totals?.subTotal ? NumericEngine.format(totals?.subTotal || 0, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                                {/* <FormatTotalNumbers inputValue={totals?.subTotal} /> */}
                            </Typography>

                            <Typography mb={1}>
                                {totals.discount ? NumericEngine.format(totals?.discount || 0, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                                {/* <FormatTotalNumbers inputValue={totals?.discount} /> */}
                            </Typography>

                            <Typography mb={1}>
                                {totals?.tax ? NumericEngine.format(totals?.tax || 0, { formatCurrency: true, trailingZeros: true }) : '$0.00'}
                                {/* <FormatTotalNumbers inputValue={totals?.tax} /> */}
                            </Typography>
                            <Typography>
                                {totals?.estimatedTotal
                                    ? NumericEngine.format(totals?.estimatedTotal || 0, { formatCurrency: true, trailingZeros: true })
                                    : '$0.00'}
                                {/* <FormatTotalNumbers inputValue={totals?.estimatedTotal} /> */}
                            </Typography>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </div>
    );
};

export default DiscountComponent;
